// AddRecipeFromSheet — universal "add a recipe" entry with three input
// pipelines:
//
//   PHOTO  — user snaps raw ingredients on a counter / cutting board.
//            We hand the image to identify-meal-ingredients (Haiku
//            vision), surface a confirm screen with the matched
//            canonicals, then bubble the seed up so the parent can
//            mount AIRecipe with those ingredients as the working
//            kitchen for this draft.
//   LINK   — user pastes a TikTok / Instagram / YouTube URL plus the
//            video's caption / description text. We send the caption
//            to parse-recipe-text (Haiku) — the link rides along as
//            attribution. We do NOT scrape the destination (per repo
//            rule against scraping branded sites).
//   TEXT   — user pastes recipe text directly (notes app, website,
//            email). Same parse-recipe-text path the existing Recipe
//            Book paste flow uses.
//
// LINK + TEXT both end in the same ReviewStage → onSave(recipe). PHOTO
// ends with onPhotoSeed(items) — the parent decides where to route
// (typically: switch CreateMenu to its AI mode with the items as the
// pantry override).
//
// Why one sheet, not three: the review/save tail is identical and the
// user's mental model is "I have something I want to turn into a
// recipe — pick a source." Mode picker lives at the top of the sheet
// and switches in-place; back button returns to the picker.

import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { FONT, COLOR } from "../lib/tokens";
import { parseRecipeText } from "../lib/parseRecipeText";
import { identifyMealIngredients } from "../lib/identifyMealIngredients";
import { transcribeVideoLink } from "../lib/transcribeVideoLink";
import { captureTrainingExample } from "../lib/captureTrainingExample";
import { findIngredient } from "../data/ingredients";

// Per-mode visual identity. Kept simple — emoji ladder + one short
// kicker — so the picker reads at a glance without competing with the
// rest of CreateMenu's hero cards.
const MODES = [
  {
    id: "photo",
    label: "Snap a photo",
    sub:   "AI identifies what's in front of you and drafts a recipe.",
    emoji: "📸",
  },
  {
    id: "link",
    label: "From a video link",
    sub:   "Paste a TikTok / Instagram / YouTube link plus the caption.",
    emoji: "🔗",
  },
  {
    id: "text",
    label: "Paste recipe text",
    sub:   "From your notes, a website, an email — anything.",
    emoji: "📝",
  },
];

export default function AddRecipeFromSheet({
  open,
  onClose,
  // (recipe) => Promise — persist the structured recipe to user_recipes
  // with source="custom". Supplied by CreateMenu, which routes through
  // useUserRecipes.saveRecipe under the hood.
  onSave,
  // (items) => void — PHOTO-mode handoff. items: [{canonicalId, name,
  // emoji, state, confidence}, ...]. Parent typically maps these to a
  // pantry-shaped array and mounts AIRecipe with them as the working
  // kitchen.
  onPhotoSeed,
  // Optional — caller's auth.uid(). When supplied we capture
  // ai_training_examples rows for the photo flow's model output vs
  // user_final pair. Fire-and-forget; missing userId means we just
  // skip capture without blocking anything.
  userId,
  // Optional URL to pre-seed the sheet in LINK mode. Used by the iOS
  // share-extension handoff — when a user picks Booda from the share
  // sheet on a TikTok / Instagram / YouTube video, the URL lands here
  // and we land them in the LINK input with the field already filled.
  initialLink = null,
  onInitialLinkConsumed,
}) {
  // (Local keyboard-offset tracking removed in the 2026-05-23 audit,
  // commit 5c/5. The outer wrapper below uses inline
  // `position: fixed` + `align-items: flex-end`, which matches the
  // layer-1 attribute selector in public/index.html — that rule
  // already adds `padding-bottom: var(--kb-offset)` automatically,
  // which lifts this flex-end-anchored inner sheet by exactly the
  // keyboard height. The previous local visualViewport listener +
  // inline translateY was a duplicate of that behavior AND on top
  // of the global rule it was double-lifting the sheet, pushing
  // the URL field above the visible viewport.)

  const [mode, setMode] = useState(null);            // null = picker
  // input | transcribing | parsing | review | saving
  const [stage, setStage] = useState("input");
  const [parsed, setParsed] = useState(null);
  // Stash the (input, original-parse) pair from the last successful
  // parse-recipe-text call so the save handler can capture a training
  // pair against whatever recipe ends up persisted. Cleared on reset.
  const [parseInputBlob, setParseInputBlob] = useState(null);
  const [parseSource, setParseSource]       = useState(null);  // "text" | "link"
  const [parseOriginal, setParseOriginal]   = useState(null);  // immutable model output
  const [errMsg, setErrMsg] = useState("");

  // Mode-specific state. Reset on sheet open so a previous attempt
  // doesn't bleed into the next one.
  const [text, setText] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  // One-shot consumption of an externally-provided link seed (e.g.
  // the iOS share-extension handoff). When `initialLink` is set we
  // force-land in LINK mode with the URL pre-filled, then signal the
  // caller via onInitialLinkConsumed so the same URL doesn't re-seed
  // on a re-open.
  useEffect(() => {
    if (!open || !initialLink) return;
    setLinkUrl(initialLink);
    setMode("link");
    onInitialLinkConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialLink]);
  // Manual fallback: when auto-transcribe fails (cobalt rate-limit,
  // private video, no spoken track) the user can paste the caption
  // and we send THAT through parse-recipe-text. Same shape as TEXT
  // mode but with the URL stamped as sourceUrl.
  const [linkCaption, setLinkCaption] = useState("");
  // Filled after auto-transcribe succeeds — shown in the review pane
  // so the user can see what Whisper heard before saving.
  const [linkTranscript, setLinkTranscript] = useState("");
  const [linkDurationSec, setLinkDurationSec] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoItems, setPhotoItems] = useState(null); // identified
  // Photo sub-mode — "raw_ingredients" = stuff on a board (default,
  // legacy behavior), "finished_dish" = reverse-engineer a plated dish
  // photo into a richer sketch (techniques, structure, finishing).
  const [photoMode, setPhotoMode] = useState("raw_ingredients");
  // Reverse-engineered sketch from finished_dish mode. Populated by
  // the identify call, surfaced as extra chip rows on PhotoReviewStage,
  // and forwarded through onPhotoSeed so the recipe generator inherits
  // the visible characteristics (3-tier ganache cake, grill-finished
  // steak, etc.) instead of inventing them.
  const [photoSketch, setPhotoSketch] = useState(null);
  // Optional user-supplied description / hint. When the user types
  // something here (e.g. "my mom's lasagna with extra cheese", "thai
  // green curry over jasmine rice"), it's passed to the identifier
  // as a strong signal — weighted the same as overlay text — so the
  // model locks the identification to what the user says it is.
  const [photoDescription, setPhotoDescription] = useState("");

  useEffect(() => {
    if (open) {
      setMode(null);
      setStage("input");
      setParsed(null);
      setErrMsg("");
      setText("");
      setLinkUrl("");
      setLinkCaption("");
      setLinkTranscript("");
      setLinkDurationSec(null);
      setPhotoFile(null);
      setPhotoPreview(null);
      setPhotoItems(null);
      setPhotoMode("raw_ingredients");
      setPhotoSketch(null);
      setPhotoDescription("");
      setParseInputBlob(null);
      setParseSource(null);
      setParseOriginal(null);
    }
  }, [open]);

  if (!open) return null;

  const reset = () => {
    setMode(null);
    setStage("input");
    setParsed(null);
    setErrMsg("");
    setParseInputBlob(null);
    setParseSource(null);
    setParseOriginal(null);
  };

  // ── PARSE handlers (LINK + TEXT share parse-recipe-text) ──────────
  const doParseText = async () => {
    setStage("parsing");
    setErrMsg("");
    try {
      const { recipe } = await parseRecipeText({ text });
      // Stash the raw input + immutable model output so the save
      // handler can capture a training pair against whatever lands
      // on the row. proposedCanonicals are already upserted into
      // pending_ingredient_info edge-side; the recipe carries them
      // inline on the row for the future propose-card UI.
      setParseInputBlob(text);
      setParseSource("text");
      setParseOriginal(recipe);
      setParsed(recipe);
      setStage("review");
    } catch (e) {
      setErrMsg(e?.message || "Couldn't parse the recipe");
      setStage("input");
    }
  };

  // LINK flow: try auto-transcribe (cobalt + Whisper) first. If that
  // succeeds, the transcript is the parse source. If it fails (rate
  // limit, private video, no spoken track), we fall back to whatever
  // the user pasted into the caption textarea. Either way, the URL
  // gets stamped as recipe.sourceUrl so the saved recipe links back
  // to the original video.
  const doParseLink = async () => {
    setErrMsg("");
    let sourceText = "";
    let transcript = "";
    let durationSec = null;

    // Normalize bare-domain URLs (`youtube.com/...`, `youtu.be/...`)
    // by prepending https://. Mobile users routinely paste/type
    // without the protocol; downstream resolvers need a valid URL.
    let url = linkUrl.trim();
    if (url && !/^https?:\/\//i.test(url)) {
      url = `https://${url}`;
    }
    const caption = linkCaption.trim();

    // Auto-transcribe path — only when we actually have a URL.
    if (url) {
      setStage("transcribing");
      try {
        const t = await transcribeVideoLink({ url });
        transcript = t.transcript || "";
        durationSec = t.durationSec ?? null;
      } catch (e) {
        // Auto-transcribe failed. If the user ALSO pasted a caption,
        // silently fall through to the caption path. If they only
        // gave us a URL, surface the error and let them retry or
        // add a caption.
        if (!caption) {
          setErrMsg(e?.message || "Couldn't transcribe — try pasting the caption below as a fallback");
          setStage("input");
          return;
        }
      }
    }

    // Build the blob we send to parse-recipe-text. Order: source URL
    // (for attribution) → transcript (preferred) → caption (fallback
    // / supplement). Both transcript AND caption ride together when
    // both exist — Haiku reconciles them.
    sourceText = [
      url        ? `Source: ${url}` : null,
      transcript ? `Transcript:\n${transcript}` : null,
      caption    ? `Caption:\n${caption}` : null,
    ].filter(Boolean).join("\n\n");

    if (sourceText.trim().length < 30) {
      setErrMsg("paste a caption or a longer link — not enough text to parse");
      setStage("input");
      return;
    }

    setStage("parsing");
    try {
      const { recipe } = await parseRecipeText({ text: sourceText });
      if (url) recipe.sourceUrl = url;
      setLinkTranscript(transcript);
      setLinkDurationSec(durationSec);
      // Same capture-prep as text-mode: stash blob + immutable model
      // output so save can record a training pair.
      setParseInputBlob(sourceText);
      setParseSource("link");
      setParseOriginal(recipe);
      setParsed(recipe);
      setStage("review");
    } catch (e) {
      setErrMsg(e?.message || "Couldn't parse the recipe");
      setStage("input");
    }
  };

  // ── PHOTO handler — identify, then hand off via onPhotoSeed ───────
  const handlePhotoFile = (file) => {
    if (!file) return;
    setPhotoFile(file);
    setErrMsg("");
    setPhotoItems(null);
    // Local-only object URL for the preview thumb.
    try {
      const url = URL.createObjectURL(file);
      setPhotoPreview(url);
    } catch { /* swallow */ }
  };

  const doIdentify = async () => {
    if (!photoFile) return;
    setStage("parsing");
    setErrMsg("");
    try {
      const result = await identifyMealIngredients({
        input: photoFile,
        mode: photoMode,
        description: photoDescription.trim() || null,
      });
      // raw_ingredients mode returns a bare array (legacy); finished_dish
      // mode returns a sketch object containing { ingredients, dishName,
      // structure, techniques, finishing, plating }.
      const isSketch = result && !Array.isArray(result) && result.mode === "finished_dish";
      const items = isSketch ? result.ingredients : result;
      if (!Array.isArray(items) || items.length === 0) {
        setErrMsg(photoMode === "finished_dish"
          ? "Couldn't read that dish — try a clearer shot of the plate"
          : "Couldn't see any food in that photo — try a clearer shot");
        setStage("input");
        return;
      }
      setPhotoItems(items);
      setPhotoSketch(isSketch ? result : null);
      setStage("review");
    } catch (e) {
      setErrMsg(e?.message || "Couldn't read the photo");
      setStage("input");
    }
  };

  const confirmPhotoSeed = (answers = null) => {
    if (!photoItems || photoItems.length === 0) return;
    // When the user answered follow-up questions, fold those answers
    // INTO the sketch as a `userAnswers` field. The downstream meal-
    // prompt composer adds them to the prompt so Claude inherits the
    // user's corrections ("user picked: Ganache, Medium-rare").
    let finalSketch = photoSketch;
    if (answers && photoSketch && Object.keys(answers).length > 0) {
      const nonEmpty = Object.fromEntries(
        Object.entries(answers).filter(([, v]) => v && typeof v === "string"),
      );
      if (Object.keys(nonEmpty).length > 0) {
        finalSketch = { ...photoSketch, userAnswers: nonEmpty };
      }
    }

    // Capture for the fine-tuning dataset. Pair the (mode, description)
    // input with the model's full output AND the user_final state
    // (sketch + any follow-up answers). The diff is the training
    // signal — what Claude should have produced on its own. Fire-
    // and-forget; never blocks the user flow.
    if (userId) {
      const hadAnswers = answers && Object.values(answers).some((v) => v);
      captureTrainingExample({
        userId,
        taskType: photoMode === "finished_dish"
          ? "photo_identify_finished_dish"
          : "photo_identify_raw_ingredients",
        input: {
          mode: photoMode,
          description: photoDescription?.trim() || null,
        },
        modelOutput: photoSketch || { ingredients: photoItems },
        userFinal:   hadAnswers ? finalSketch : null,
        correctionKind: hadAnswers ? "edit" : "accept",
        modelVersion: "identify-meal-ingredients@v6",  // bump on prompt revisions
        metadata: {
          itemCount: photoItems.length,
          followUpsAnswered: hadAnswers
            ? Object.keys(answers).filter((k) => answers[k]).length
            : 0,
        },
      });
    }

    // Pass items as the first arg (legacy contract) plus the sketch
    // metadata as a second arg. Existing call sites that ignore the
    // second arg still work. New consumers can use the sketch to
    // pre-fill the meal prompt or shape generate-recipe.
    onPhotoSeed?.(photoItems, { sketch: finalSketch });
    onClose?.();
  };

  // ── SAVE handler (LINK + TEXT) ────────────────────────────────────
  const doSave = async () => {
    if (!parsed) return;
    setStage("saving");
    setErrMsg("");
    try {
      await onSave?.(parsed);

      // Capture training pair — pasted/transcribed input → parsed
      // recipe. Diff between parseOriginal (immutable model output)
      // and `parsed` (what we're saving) signals whether the user
      // edited fields. ReviewStage is read-only today so the diff
      // is usually empty (correction_kind: "accept"); when inline
      // editing lands, "edit" pairs will start flowing.
      if (userId && parseInputBlob && parseOriginal) {
        const sameJson =
          JSON.stringify(parsed) === JSON.stringify(parseOriginal);
        captureTrainingExample({
          userId,
          taskType: parseSource === "link"
            ? "recipe_parse_link"
            : "recipe_parse_text",
          input: { text: parseInputBlob, source: parseSource },
          modelOutput: parseOriginal,
          userFinal:   sameJson ? null : parsed,
          correctionKind: sameJson ? "accept" : "edit",
          modelVersion: "parse-recipe-text@v1",
          metadata: {
            ingredientCount: Array.isArray(parsed.ingredients) ? parsed.ingredients.length : 0,
            stepCount:       Array.isArray(parsed.steps)       ? parsed.steps.length       : 0,
            hasTranscript:   parseSource === "link" && parseInputBlob.includes("Transcript:"),
            hasCaption:      parseSource === "link" && parseInputBlob.includes("Caption:"),
            hasSourceUrl:    !!parsed.sourceUrl,
          },
        });
      }

      onClose?.();
    } catch (e) {
      setErrMsg(e?.message || "Couldn't save");
      setStage("review");
    }
  };

  return createPortal(
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0,
        zIndex: 340,
        background: "rgba(20, 14, 8, 0.78)",
        backdropFilter: "blur(8px) saturate(140%)",
        WebkitBackdropFilter: "blur(8px) saturate(140%)",
        display: "flex", alignItems: "flex-end", justifyContent: "center",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%", maxWidth: 480,
          background: "linear-gradient(180deg, #FBF5E8 0%, #F2E9D2 100%)",
          borderRadius: "18px 18px 0 0",
          padding: "20px 22px calc(env(safe-area-inset-bottom, 0px) + 22px)",
          // Fixed-height drawer that does NOT shrink when the iOS
          // keyboard appears. `vh` units shrink with the visual
          // viewport (so 92vh collapses to ~46vh when the keyboard
          // takes half the screen); `svh` (small viewport height)
          // stays locked to the no-keyboard size. The drawer keeps
          // its full height even when typing — content scrolls
          // internally if the keyboard occludes part of it.
          height: "min(720px, 92svh)",
          display: "flex", flexDirection: "column", gap: 14,
          boxShadow: "0 -10px 30px rgba(20,14,8,0.40)",
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          // Keyboard lift is owned by the outer flex-end backdrop's
          // layer-1 padding-bottom — see comment above. No local
          // transform here anymore.
        }}
      >
        <div style={{ width: 36, height: 4, background: "rgba(124,94,44,0.30)", borderRadius: 2, margin: "0 auto" }} />

        {/* MODE PICKER */}
        {mode === null && (
          <ModePicker
            onPick={(id) => { setMode(id); setStage("input"); }}
            onClose={onClose}
          />
        )}

        {/* PHOTO INPUT */}
        {mode === "photo" && stage === "input" && (
          <PhotoStage
            file={photoFile}
            previewUrl={photoPreview}
            onPick={handlePhotoFile}
            errMsg={errMsg}
            onBack={reset}
            onIdentify={doIdentify}
            photoMode={photoMode}
            setPhotoMode={setPhotoMode}
            description={photoDescription}
            setDescription={setPhotoDescription}
          />
        )}

        {/* LINK INPUT */}
        {mode === "link" && stage === "input" && (
          <LinkStage
            url={linkUrl}
            setUrl={setLinkUrl}
            caption={linkCaption}
            setCaption={setLinkCaption}
            errMsg={errMsg}
            onBack={reset}
            onParse={doParseLink}
          />
        )}

        {/* TEXT INPUT */}
        {mode === "text" && stage === "input" && (
          <TextStage
            text={text}
            setText={setText}
            errMsg={errMsg}
            onBack={reset}
            onParse={doParseText}
          />
        )}

        {/* TRANSCRIBING — link-only stage between input and parsing */}
        {stage === "transcribing" && (
          <div style={{ padding: "60px 20px", textAlign: "center" }}>
            <div style={{
              fontFamily: FONT.forma, fontSize: 9, fontWeight: 600,
              color: "#9C7E40", letterSpacing: "0.18em",
            }}>
              LISTENING TO THE VIDEO
            </div>
            <div style={{
              marginTop: 8, fontFamily: FONT.itemTitle, fontSize: 22,
              fontStyle: "italic", color: "#3A2A12",
            }}>
              Pulling the audio…
            </div>
            <div style={{
              marginTop: 6, fontFamily: FONT.forma, fontSize: 11,
              color: "#7C5E2C",
            }}>
              This usually takes 10-20 seconds.
            </div>
          </div>
        )}

        {/* PARSING — shared loader */}
        {stage === "parsing" && (
          <div style={{ padding: "60px 20px", textAlign: "center" }}>
            <div style={{
              fontFamily: FONT.forma, fontSize: 9, fontWeight: 600,
              color: "#9C7E40", letterSpacing: "0.18em",
            }}>
              {mode === "photo" ? "READING YOUR PHOTO" : "READING YOUR RECIPE"}
            </div>
            <div style={{
              marginTop: 8, fontFamily: FONT.itemTitle, fontSize: 22,
              fontStyle: "italic", color: "#3A2A12",
            }}>
              One moment…
            </div>
          </div>
        )}

        {/* PHOTO REVIEW — confirm the identified ingredients */}
        {mode === "photo" && stage === "review" && photoItems && (
          <PhotoReviewStage
            items={photoItems}
            sketch={photoSketch}
            previewUrl={photoPreview}
            errMsg={errMsg}
            onBack={() => { setStage("input"); setPhotoItems(null); setPhotoSketch(null); }}
            onConfirm={confirmPhotoSeed}
          />
        )}

        {/* TEXT/LINK REVIEW — parsed recipe summary */}
        {(mode === "link" || mode === "text") && stage === "review" && parsed && (
          <ReviewStage
            parsed={parsed}
            transcript={mode === "link" ? linkTranscript : ""}
            durationSec={mode === "link" ? linkDurationSec : null}
            errMsg={errMsg}
            onTryAgain={() => {
              // Capture as "regenerate" — user saw the parse, didn't
              // like it, asked for another shot. Negative signal on
              // the original model output. Don't clear parseInputBlob /
              // parseOriginal so a subsequent save still captures
              // against the latest parse.
              if (userId && parseInputBlob && parseOriginal) {
                captureTrainingExample({
                  userId,
                  taskType: parseSource === "link"
                    ? "recipe_parse_link"
                    : "recipe_parse_text",
                  input: { text: parseInputBlob, source: parseSource },
                  modelOutput: parseOriginal,
                  userFinal: null,
                  correctionKind: "regenerate",
                  modelVersion: "parse-recipe-text@v1",
                });
              }
              setStage("input");
              setParsed(null);
              setParseOriginal(null);
            }}
            onSave={doSave}
          />
        )}

        {stage === "saving" && (
          <div style={{ padding: "60px 20px", textAlign: "center" }}>
            <div style={{
              fontFamily: FONT.itemTitle, fontSize: 22,
              fontStyle: "italic", color: "#3A2A12",
            }}>
              Adding to your recipes…
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

// ── Mode picker ─────────────────────────────────────────────────────

function ModePicker({ onPick, onClose }) {
  return (
    <>
      <div>
        <div style={{
          fontFamily: FONT.forma, fontSize: 9, fontWeight: 600,
          color: "#9C7E40", letterSpacing: "0.18em",
        }}>
          ADD A RECIPE
        </div>
        <div style={{
          marginTop: 4, fontFamily: FONT.itemTitle, fontSize: 24,
          fontStyle: "italic", color: "#3A2A12", lineHeight: 1.2,
        }}>
          What are we starting from?
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 4 }}>
        {MODES.map((m) => (
          <button
            key={m.id}
            onClick={() => onPick(m.id)}
            className="mcm-focusable"
            style={{
              display: "flex", alignItems: "center", gap: 14,
              padding: "14px 16px",
              background: "#FFFCF1",
              border: "1px solid rgba(124,94,44,0.30)",
              borderRadius: 12,
              cursor: "pointer", textAlign: "left",
            }}
          >
            <div style={{ fontSize: 28, lineHeight: 1, flexShrink: 0 }}>{m.emoji}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontFamily: FONT.itemTitle, fontSize: 17,
                fontStyle: "italic", color: "#3A2A12",
              }}>
                {m.label}
              </div>
              <div style={{
                marginTop: 2, fontFamily: FONT.forma, fontSize: 11,
                color: "#7C5E2C", lineHeight: 1.4,
              }}>
                {m.sub}
              </div>
            </div>
            <span style={{ color: "#9C7E40", fontFamily: FONT.forma, fontSize: 14 }}>→</span>
          </button>
        ))}
      </div>

      <button
        onClick={onClose}
        style={{
          marginTop: 4, padding: "12px",
          background: "transparent",
          border: "1px solid rgba(124,94,44,0.30)",
          color: "#5C3F18",
          borderRadius: 10,
          fontFamily: FONT.forma, fontSize: 11, fontWeight: 600,
          letterSpacing: "0.12em", textTransform: "uppercase",
          cursor: "pointer",
        }}
      >
        Cancel
      </button>
    </>
  );
}

// ── PHOTO stage ─────────────────────────────────────────────────────

function PhotoStage({ file, previewUrl, onPick, errMsg, onBack, onIdentify, photoMode, setPhotoMode, description, setDescription }) {
  const valid = !!file;
  const isFinished = photoMode === "finished_dish";
  const renderModeTab = (id, label, sub) => {
    const active = photoMode === id;
    return (
      <button
        key={id}
        type="button"
        onClick={() => setPhotoMode?.(id)}
        className="mcm-focusable"
        style={{
          flex: 1,
          padding: "10px 12px",
          borderRadius: 10,
          border: `1px solid ${active ? "#7C5E2C" : "rgba(124,94,44,0.30)"}`,
          background: active ? "rgba(124,94,44,0.12)" : "#FFFCF1",
          color: active ? "#3A2A12" : "#7C5E2C",
          cursor: "pointer",
          touchAction: "manipulation",
          textAlign: "left",
        }}
      >
        <div style={{
          fontFamily: FONT.forma, fontSize: 11, fontWeight: 700,
          letterSpacing: "0.12em", textTransform: "uppercase",
        }}>
          {label}
        </div>
        <div style={{
          fontFamily: FONT.detail, fontSize: 12, fontStyle: "italic",
          color: active ? "#5C411C" : "#9C7E40",
          marginTop: 2, lineHeight: 1.3,
        }}>
          {sub}
        </div>
      </button>
    );
  };
  return (
    <>
      <Header
        kicker={isFinished ? "SNAP A FINISHED DISH" : "SNAP YOUR INGREDIENTS"}
        title={isFinished ? "Show me what you want to make" : "Show me what you have"}
        sub={isFinished
          ? "Photograph the plated / finished dish you want to recreate — we'll reverse-engineer the ingredients, techniques (grill marks → grill, glossy drip → ganache, etc.), and structure into a recipe draft."
          : "Lay out your ingredients on a counter or cutting board and snap a clear photo. We'll identify them and draft a recipe around what's there."}
        onBack={onBack}
      />

      {/* Mode toggle — segmented control. The two modes share the camera
          input below but fire different prompts on identify. */}
      <div style={{ display: "flex", gap: 8, marginBottom: 4 }}>
        {renderModeTab("raw_ingredients", "Raw ingredients", "Stuff on the board")}
        {renderModeTab("finished_dish",   "Finished dish",   "Recreate a plate")}
      </div>

      <label
        htmlFor="ar-photo-input"
        className="mcm-focusable"
        style={{
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          gap: 8,
          padding: previewUrl ? 0 : "32px 16px",
          minHeight: previewUrl ? 0 : 180,
          background: previewUrl ? "transparent" : "#FFFCF1",
          border: previewUrl ? "none" : "1px dashed rgba(124,94,44,0.40)",
          borderRadius: 12,
          cursor: "pointer",
          overflow: "hidden",
        }}
      >
        {previewUrl ? (
          <img
            src={previewUrl}
            alt="ingredients preview"
            style={{
              width: "100%", maxHeight: 320, objectFit: "cover",
              borderRadius: 12, border: "1px solid rgba(124,94,44,0.30)",
            }}
          />
        ) : (
          <>
            <div style={{ fontSize: 38, lineHeight: 1 }}>📸</div>
            <div style={{
              fontFamily: FONT.itemTitle, fontSize: 16,
              fontStyle: "italic", color: "#3A2A12",
            }}>
              Tap to take or upload a photo
            </div>
            <div style={{
              fontFamily: FONT.forma, fontSize: 11,
              color: "#7C5E2C",
            }}>
              Steak, herbs, produce — whatever's on the counter
            </div>
          </>
        )}
      </label>
      <input
        id="ar-photo-input"
        type="file"
        accept="image/*"
        onChange={(e) => onPick(e.target.files?.[0] || null)}
        style={{ display: "none" }}
      />

      {/* Optional description — when filled, sent to the identifier
          as a strong hint. Useful when the photo's ambiguous (low
          light, overlapping items, novel dish) or when the user
          wants to lock the identification to a specific thing
          ("my mom's lasagna with extra cheese", "thai green curry
          over jasmine rice", "tonkatsu donburi"). Treated like an
          overlay-text answer — model uses it as ground truth. */}
      <div style={{
        fontFamily: FONT.forma, fontSize: 10, fontWeight: 600,
        color: "#9C7E40", letterSpacing: "0.14em",
        textTransform: "uppercase", marginTop: 6,
      }}>
        Optional · tell us what this is
      </div>
      <input
        value={description || ""}
        onChange={(e) => setDescription?.(e.target.value)}
        placeholder={isFinished
          ? 'e.g. "tonkatsu donburi", "my grandma\'s lasagna"'
          : 'e.g. "ingredients for chana masala", "taco night"'}
        autoCapitalize="off"
        autoCorrect="off"
        style={{
          width: "100%", boxSizing: "border-box",
          padding: "10px 14px",
          background: "#FFFCF1",
          border: "1px solid rgba(124,94,44,0.30)",
          borderRadius: 10,
          fontFamily: FONT.forma, fontSize: 13,
          color: "#3A2A12", outline: "none",
        }}
      />

      {errMsg && <ErrorBox msg={errMsg} />}

      <ButtonRow
        secondary={{ label: "Back", onClick: onBack }}
        primary={{
          label: previewUrl ? "Identify" : "Choose a photo",
          onClick: valid ? onIdentify : null,
          disabled: !valid,
        }}
      />
    </>
  );
}

function PhotoReviewStage({ items, sketch, previewUrl, errMsg, onBack, onConfirm }) {
  const isFinishedDish = !!sketch;
  // Per-question answer state. Map of question.id → chosen option
  // string. Initial render leaves them all blank (no default pick)
  // so the user actively commits an answer before drafting.
  const [answers, setAnswers] = useState({});
  const followUps = Array.isArray(sketch?.followUpQuestions) ? sketch.followUpQuestions : [];

  // Compact chip used for technique / structure / finishing detail. Keep
  // it visually quieter than the ingredient rows so the ingredient list
  // stays the headline; these chips are the bonus context Claude saw.
  const Chip = ({ kicker, value }) => (
    <div style={{
      display: "inline-flex", alignItems: "baseline", gap: 6,
      padding: "5px 10px",
      borderRadius: 999,
      background: "rgba(124,94,44,0.08)",
      border: "1px solid rgba(124,94,44,0.25)",
    }}>
      <span style={{
        fontFamily: FONT.forma, fontSize: 9, fontWeight: 700,
        color: "#7C5E2C", letterSpacing: "0.14em", textTransform: "uppercase",
      }}>{kicker}</span>
      <span style={{
        fontFamily: FONT.forma, fontSize: 12, fontWeight: 500,
        color: "#3A2A12",
      }}>{value}</span>
    </div>
  );

  // Render the sketch's structural/technique/sauce/finishing signals
  // into a horizontal chip strip. Skips empty fields. Sauce chips
  // surface alternatives as a quieter suffix ("chimichurri · or
  // salsa_verde / pesto") so the user can correct an ambiguous read
  // by glancing at the chip.
  const sketchChips = (() => {
    if (!sketch) return [];
    const chips = [];
    const s = sketch.structure || {};
    if (s.layers != null)    chips.push({ kicker: "LAYERS",    value: String(s.layers) });
    if (s.shape)             chips.push({ kicker: "SHAPE",     value: String(s.shape).replace(/_/g, " ") });
    if (s.diameter)          chips.push({ kicker: "SIZE",      value: String(s.diameter) });
    if (s.crustType)         chips.push({ kicker: "CRUST",     value: String(s.crustType).replace(/_/g, " ") });
    for (const t of (sketch.techniques || [])) {
      chips.push({ kicker: "TECHNIQUE", value: String(t).replace(/_/g, " ") });
    }
    for (const sa of (sketch.sauces || [])) {
      if (!sa || !sa.name) continue;
      const alts = Array.isArray(sa.alternatives) && sa.alternatives.length > 0
        ? ` · or ${sa.alternatives.map((a) => String(a).replace(/_/g, " ")).join(" / ")}`
        : "";
      chips.push({
        kicker: "SAUCE",
        value: `${String(sa.name).replace(/_/g, " ")}${alts}`,
      });
    }
    const f = sketch.finishing || {};
    if (f.type && f.type !== "none") chips.push({ kicker: "FINISH", value: String(f.type).replace(/_/g, " ") });
    return chips;
  })();

  return (
    <>
      <Header
        kicker={isFinishedDish ? "HERE'S WHAT WE READ" : "HERE'S WHAT WE SAW"}
        title={isFinishedDish
          ? (sketch.dishName || "We reverse-engineered the dish")
          : "Look right?"}
        sub={isFinishedDish
          ? "We pulled the visible ingredients plus the techniques and structure we saw. The recipe generator will use all of this to draft something that matches the photo."
          : "We'll hand these to the AI recipe generator as the kitchen for this draft. You can adjust ingredients in the next screen."}
        onBack={onBack}
      />

      {previewUrl && (
        <img
          src={previewUrl}
          alt="ingredients"
          style={{
            width: "100%", maxHeight: 180, objectFit: "cover",
            borderRadius: 12, border: "1px solid rgba(124,94,44,0.30)",
          }}
        />
      )}

      {/* Sketch chips — only render for finished-dish mode. Shows
          structure / technique / finishing tags pulled from the photo
          so the user can confirm the model read the right signals. */}
      {sketchChips.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
          {sketchChips.map((c, i) => (
            <Chip key={`${c.kicker}-${i}`} kicker={c.kicker} value={c.value} />
          ))}
        </div>
      )}

      {sketch?.plating && (
        <div style={{
          fontFamily: FONT.detail, fontStyle: "italic", fontSize: 13,
          color: "#7C5E2C", padding: "4px 6px",
        }}>
          Plating · {sketch.plating}
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {items.map((it, i) => {
          const known = !!it.canonicalId && !!findIngredient(it.canonicalId);
          return (
            <div
              key={`${it.canonicalId || it.name}-${i}`}
              style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: "10px 12px",
                background: "#FFFCF1",
                border: "1px solid rgba(124,94,44,0.22)",
                borderRadius: 10,
              }}
            >
              <div style={{ fontSize: 22, lineHeight: 1 }}>{it.emoji || "🍽️"}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontFamily: FONT.itemTitle, fontSize: 15,
                  fontStyle: "italic", color: "#3A2A12",
                }}>
                  {it.name}
                </div>
                <div style={{
                  marginTop: 1, fontFamily: FONT.forma, fontSize: 10,
                  color: "#7C5E2C", letterSpacing: "0.06em",
                  textTransform: "uppercase",
                }}>
                  {it.state ? `${it.state} · ` : ""}
                  {it.confidence}
                  {!known ? " · NEW" : ""}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Follow-up questions — only present on finished_dish mode when
          the model flagged something ambiguous (ganache vs buttercream,
          chimichurri vs salsa verde, doneness, etc.). Each renders as
          a question card with multiple-choice options. Answers fold
          into the sketch before handoff so the recipe inherits the
          user's correction. Skippable — questions don't block confirm. */}
      {followUps.length > 0 && (
        <div style={{
          marginTop: 10,
          padding: 12,
          borderRadius: 12,
          background: "rgba(124,94,44,0.07)",
          border: "1px solid rgba(124,94,44,0.20)",
          display: "flex", flexDirection: "column", gap: 14,
        }}>
          <div style={{
            fontFamily: FONT.forma, fontSize: 10, fontWeight: 700,
            color: "#7C5E2C", letterSpacing: "0.16em", textTransform: "uppercase",
          }}>
            A couple quick checks
          </div>
          {followUps.map((q) => (
            <div key={q.id} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <div style={{
                fontFamily: FONT.itemTitle, fontSize: 15,
                fontStyle: "italic", color: "#3A2A12",
              }}>
                {q.question}
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {(q.options || []).map((opt, i) => {
                  const active = answers[q.id] === opt;
                  return (
                    <button
                      key={`${q.id}-${i}`}
                      type="button"
                      onClick={() => setAnswers((prev) => ({ ...prev, [q.id]: active ? null : opt }))}
                      className="mcm-focusable"
                      style={{
                        padding: "7px 12px",
                        borderRadius: 999,
                        border: `1px solid ${active ? "#7C5E2C" : "rgba(124,94,44,0.30)"}`,
                        background: active ? "rgba(124,94,44,0.18)" : "#FFFCF1",
                        color: active ? "#3A2A12" : "#7C5E2C",
                        fontFamily: FONT.forma, fontSize: 12, fontWeight: 500,
                        cursor: "pointer",
                        touchAction: "manipulation",
                        textAlign: "left",
                      }}
                    >
                      {opt}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {errMsg && <ErrorBox msg={errMsg} />}

      <ButtonRow
        secondary={{ label: "Try again", onClick: onBack }}
        primary={{
          label: "Use these · Draft a recipe",
          onClick: () => onConfirm?.(answers),
        }}
      />
    </>
  );
}

// ── LINK stage ──────────────────────────────────────────────────────

function LinkStage({ url, setUrl, caption, setCaption, errMsg, onBack, onParse }) {
  // Either a URL (we'll auto-transcribe) OR enough caption text to
  // parse on its own is sufficient. URL alone is the happy path; the
  // caption is a fallback for when the video has no spoken track or
  // auto-transcribe trips on a private / region-locked link.
  //
  // Accept bare-domain URLs (`youtube.com/watch?v=…`, `youtu.be/…`,
  // `tiktok.com/…`, `instagram.com/reel/…`) too — most users type
  // those without the `https://` prefix on mobile. The onParse path
  // normalizes by prepending the protocol when missing so downstream
  // resolvers (`new URL(...)`, the edge function, etc.) get a valid
  // URL either way.
  const trimmed    = (url || "").trim();
  const hasUrl     = /^(https?:\/\/|(?:www\.|m\.)?(?:youtube\.com|youtu\.be|tiktok\.com|vm\.tiktok\.com|instagram\.com|facebook\.com|fb\.watch|reels?\.)[^\s]*)/i.test(trimmed);
  const hasCaption = (caption || "").trim().length >= 30;
  const valid      = hasUrl || hasCaption;

  // Native share sheets on TikTok / Instagram / Reels ship the link
  // wrapped in marketing copy and emoji ("Check this out 🔥🔥
  // https://vm.tiktok.com/abc/ via @user"). Extract the URL on paste
  // so the field always lands a clean, parseable URL — no manual
  // surgery from the user.
  const handleUrlPaste = (e) => {
    const pasted = e.clipboardData?.getData("text") || "";
    const match = pasted.match(/https?:\/\/[^\s<>"'`)\]}]+/i);
    if (match) {
      e.preventDefault();
      // Strip trailing punctuation that commonly clings to URLs in
      // share-text ("…video/123!", "…link.").
      const cleaned = match[0].replace(/[.,!?;:]+$/g, "");
      setUrl(cleaned);
    }
  };
  return (
    <>
      <Header
        kicker="FROM A VIDEO LINK"
        title="Paste a TikTok, Reel, or YouTube link"
        sub="We'll pull the audio, transcribe it, and turn the recipe into a saved page. If the video has no voiceover, paste the caption below as a backup."
        onBack={onBack}
      />

      <input
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        onPaste={handleUrlPaste}
        placeholder="Paste a link — we'll strip the share-text junk"
        autoCapitalize="off"
        autoCorrect="off"
        spellCheck={false}
        style={{
          width: "100%", boxSizing: "border-box",
          padding: "12px 14px",
          background: "#FFFCF1",
          border: "1px solid rgba(124,94,44,0.30)",
          borderRadius: 10,
          fontFamily: FONT.forma, fontSize: 13,
          color: "#3A2A12", outline: "none",
        }}
      />

      <div style={{
        fontFamily: FONT.forma, fontSize: 10, fontWeight: 600,
        color: "#9C7E40", letterSpacing: "0.14em",
        textTransform: "uppercase", marginTop: 4,
      }}>
        Optional · caption fallback
      </div>
      <textarea
        value={caption}
        onChange={(e) => setCaption(e.target.value)}
        placeholder="If the video is silent or auto-transcribe fails, paste the caption / description here."
        rows={5}
        style={{
          width: "100%", boxSizing: "border-box",
          padding: "12px 14px",
          background: "#FFFCF1",
          border: "1px solid rgba(124,94,44,0.30)",
          borderRadius: 10,
          fontFamily: FONT.forma, fontSize: 13,
          lineHeight: 1.6, color: "#3A2A12",
          outline: "none",
          resize: "vertical",
          minHeight: 100, maxHeight: 240,
        }}
      />

      {errMsg && <ErrorBox msg={errMsg} />}

      <ButtonRow
        secondary={{ label: "Back", onClick: onBack }}
        primary={{
          label: hasUrl ? "Transcribe & read" : "Read it",
          onClick: valid ? onParse : null,
          disabled: !valid,
        }}
      />
    </>
  );
}

// ── TEXT stage ──────────────────────────────────────────────────────

function TextStage({ text, setText, errMsg, onBack, onParse }) {
  const valid = (text || "").trim().length >= 30;
  return (
    <>
      <Header
        kicker="PASTE A RECIPE"
        title="From your notes, a website, an email"
        sub="We'll extract the structure and set it up like the rest of your recipes."
        onBack={onBack}
      />

      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="2 lbs chicken thighs&#10;1 head garlic, halved&#10;1 lemon, sliced&#10;…"
        rows={8}
        style={{
          width: "100%", boxSizing: "border-box",
          padding: "12px 14px",
          background: "#FFFCF1",
          border: "1px solid rgba(124,94,44,0.30)",
          borderRadius: 10,
          fontFamily: FONT.forma, fontSize: 13,
          lineHeight: 1.6, color: "#3A2A12",
          outline: "none",
          resize: "vertical",
          minHeight: 160, maxHeight: 320,
        }}
      />

      {errMsg && <ErrorBox msg={errMsg} />}

      <ButtonRow
        secondary={{ label: "Back", onClick: onBack }}
        primary={{ label: "Read it", onClick: valid ? onParse : null, disabled: !valid }}
      />
    </>
  );
}

// ── Shared review for parsed-recipe modes (LINK + TEXT) ─────────────

function ReviewStage({ parsed, transcript, durationSec, errMsg, onTryAgain, onSave }) {
  const ingCount  = Array.isArray(parsed.ingredients) ? parsed.ingredients.length : 0;
  const stepCount = Array.isArray(parsed.steps)       ? parsed.steps.length       : 0;
  const totalMin  = (parsed.time?.prep || 0) + (parsed.time?.cook || 0);
  const [transcriptOpen, setTranscriptOpen] = useState(false);
  const hasTranscript = !!(transcript && transcript.trim());
  return (
    <>
      <div>
        <div style={{
          fontFamily: FONT.forma, fontSize: 9, fontWeight: 600,
          color: "#9C7E40", letterSpacing: "0.18em",
        }}>
          PARSED · LOOKS GOOD?
        </div>
        <div style={{
          marginTop: 4, fontFamily: FONT.itemTitle, fontSize: 24,
          fontStyle: "italic", color: "#3A2A12", lineHeight: 1.2,
        }}>
          {parsed.title || "Your recipe"}
        </div>
        {parsed.subtitle && (
          <div style={{
            marginTop: 4, fontFamily: FONT.forma, fontSize: 12,
            color: "#7C5E2C", lineHeight: 1.5,
          }}>
            {parsed.subtitle}
          </div>
        )}
      </div>

      <div style={{
        display: "flex", flexDirection: "column", gap: 4,
        padding: "12px 14px",
        background: "#FFFCF1",
        border: "1px solid rgba(124,94,44,0.22)",
        borderRadius: 10,
      }}>
        <ReviewRow label="Cuisine"     value={(parsed.cuisine    || "—").toUpperCase()} />
        <ReviewRow label="Course"      value={(parsed.course     || "—").toUpperCase()} />
        <ReviewRow label="Serves"      value={parsed.serves || "—"} />
        <ReviewRow label="Total time"  value={totalMin ? `${totalMin} min` : "—"} />
        <ReviewRow label="Ingredients" value={ingCount} />
        <ReviewRow label="Steps"       value={stepCount} />
        {parsed.sourceUrl && (
          <ReviewRow label="Source" value={shortUrl(parsed.sourceUrl)} />
        )}
        {durationSec ? (
          <ReviewRow label="Audio" value={`${durationSec}s transcribed`} />
        ) : null}
      </div>

      {hasTranscript && (
        <div style={{
          background: "#FFFCF1",
          border: "1px solid rgba(124,94,44,0.22)",
          borderRadius: 10,
          overflow: "hidden",
        }}>
          <button
            onClick={() => setTranscriptOpen((o) => !o)}
            style={{
              width: "100%",
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "10px 14px",
              background: "transparent",
              border: "none",
              cursor: "pointer",
              fontFamily: FONT.forma, fontSize: 10, fontWeight: 600,
              color: "#9C7E40", letterSpacing: "0.14em",
              textTransform: "uppercase",
              textAlign: "left",
            }}
          >
            <span>What we heard</span>
            <span style={{ fontSize: 12, color: "#7C5E2C" }}>{transcriptOpen ? "▴" : "▾"}</span>
          </button>
          {transcriptOpen && (
            <div style={{
              padding: "0 14px 12px",
              fontFamily: FONT.forma, fontSize: 12,
              lineHeight: 1.55, color: "#3A2A12",
              maxHeight: 200, overflowY: "auto",
              whiteSpace: "pre-wrap",
            }}>
              {transcript}
            </div>
          )}
        </div>
      )}

      {errMsg && <ErrorBox msg={errMsg} />}

      <ButtonRow
        secondary={{ label: "Try again", onClick: onTryAgain }}
        primary={{ label: "Save to recipes", onClick: onSave }}
      />
    </>
  );
}

function ReviewRow({ label, value }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
      <span style={{
        fontFamily: FONT.forma, fontSize: 10, fontWeight: 600,
        color: "#9C7E40", letterSpacing: "0.12em", textTransform: "uppercase",
      }}>
        {label}
      </span>
      <span style={{
        fontFamily: FONT.forma, fontSize: 12, color: "#3A2A12",
      }}>
        {value}
      </span>
    </div>
  );
}

function shortUrl(u) {
  try {
    const url = new URL(u);
    return url.hostname.replace(/^www\./, "");
  } catch {
    return u.length > 32 ? u.slice(0, 30) + "…" : u;
  }
}

// ── Shared bits ─────────────────────────────────────────────────────

function Header({ kicker, title, sub, onBack }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
      <button
        onClick={onBack}
        aria-label="Back"
        style={{
          flexShrink: 0,
          width: 30, height: 30,
          background: "transparent",
          border: "1px solid rgba(124,94,44,0.30)",
          borderRadius: 15,
          color: "#5C3F18",
          fontFamily: "system-ui, sans-serif",
          fontSize: 16, lineHeight: 1,
          cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}
      >
        ←
      </button>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontFamily: FONT.forma, fontSize: 9, fontWeight: 600,
          color: "#9C7E40", letterSpacing: "0.18em",
        }}>
          {kicker}
        </div>
        <div style={{
          marginTop: 4, fontFamily: FONT.itemTitle, fontSize: 22,
          fontStyle: "italic", color: "#3A2A12", lineHeight: 1.2,
        }}>
          {title}
        </div>
        {sub && (
          <div style={{
            marginTop: 6, fontFamily: FONT.forma, fontSize: 12,
            color: "#7C5E2C", lineHeight: 1.5,
          }}>
            {sub}
          </div>
        )}
      </div>
    </div>
  );
}

function ErrorBox({ msg }) {
  return (
    <div style={{
      fontFamily: FONT.forma, fontSize: 11, color: "#A04230",
      padding: "8px 12px",
      background: "rgba(180,60,40,0.10)",
      border: "1px solid rgba(180,60,40,0.30)",
      borderRadius: 8,
    }}>
      {msg}
    </div>
  );
}

function ButtonRow({ secondary, primary }) {
  const disabled = !!primary?.disabled;
  return (
    <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
      {secondary && (
        <button
          onClick={secondary.onClick}
          style={{
            flex: 1, padding: "12px",
            background: "transparent",
            border: "1px solid rgba(124,94,44,0.30)",
            color: "#5C3F18",
            borderRadius: 10,
            fontFamily: FONT.forma, fontSize: 11, fontWeight: 600,
            letterSpacing: "0.12em", textTransform: "uppercase",
            cursor: "pointer",
          }}
        >
          {secondary.label}
        </button>
      )}
      {primary && (
        <button
          onClick={disabled ? undefined : primary.onClick}
          disabled={disabled}
          style={{
            flex: 2, padding: "12px",
            background: disabled ? "rgba(124,94,44,0.20)" : "#2F8F83",
            border: `1px solid ${disabled ? "rgba(124,94,44,0.30)" : "#246A60"}`,
            color: disabled ? "rgba(60,40,18,0.45)" : "#FFFFFF",
            borderRadius: 10,
            fontFamily: FONT.forma, fontSize: 12, fontWeight: 600,
            letterSpacing: "0.14em", textTransform: "uppercase",
            cursor: disabled ? "default" : "pointer",
          }}
        >
          {primary.label}
        </button>
      )}
    </div>
  );
}
